import { useCallback, ChangeEvent, SyntheticEvent } from "react"
import { useTranslation } from "react-i18next"

import Selector from "@/components/form/selector/Selector"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import Toggle from "@/components/form/toggle/Toggle"

import type { ChartOptionsPanelProps } from "./types"
import { SingleMetricSelector, MetricSelector } from "./SharedWidgets"

export default function ComparisonOptions(props: ChartOptionsPanelProps) {
  const { t } = useTranslation()
  const {
    item,
    onDataSourceChange,
    onOptionChange,
    availableSessions,
    availablePlayers,
    itemSessionIds,
    itemPlayerIds,
    metricOptions,
    translateKey,
  } = props

  const handleSessionChange = useCallback(
    (ids: number[]) => {
      onDataSourceChange("sessionIds", ids)
    },
    [onDataSourceChange],
  )

  const handlePlayerChange = useCallback(
    (ids: number[]) => {
      onDataSourceChange("playerIds", ids)
    },
    [onDataSourceChange],
  )

  const handleMetricChange = useCallback(
    (metricKey: string) => {
      onDataSourceChange("metrics", [metricKey])
    },
    [onDataSourceChange],
  )

  const comparisonTypeOptions = [
    { value: "pie", label: t("pieDonut", "Pie / Donut") },
    { value: "radar", label: t("radar", "Radar") },
    { value: "treemap", label: t("treemap", "Treemap") },
  ]

  const currentComparisonType = item.options.comparisonType ?? "pie"
  const isSinglePlayerMode = itemPlayerIds.length === 1

  return (
    <>
      <div className="PropertyPanel__field">
        <Selector
          slotProps={{ popper: { className: "PropertyPanel__menu" } }}
          label={t("comparisonType", "Comparison Type")}
          options={comparisonTypeOptions}
          value={
            comparisonTypeOptions.find((o) => o.value === currentComparisonType) ||
            comparisonTypeOptions[0]
          }
          onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
            if (newVal) onOptionChange("comparisonType", newVal.value)
          }}
          placeholder={t("select")}
        />
      </div>

      {isSinglePlayerMode ? (
        <MetricSelector
          label={t("metrics", "Metrics")}
          selectedMetrics={item.dataSource.metrics ?? []}
          metricOptions={metricOptions}
          onAdd={(metricKey) =>
            onDataSourceChange("metrics", [...(item.dataSource.metrics ?? []), metricKey])
          }
          onRemove={(metricKey) =>
            onDataSourceChange(
              "metrics",
              (item.dataSource.metrics ?? []).filter((k) => k !== metricKey),
            )
          }
          translateKey={translateKey}
        />
      ) : (
        <SingleMetricSelector
          label={t("metrics", "Metrics")}
          value={item.dataSource.metrics?.[0]}
          metricOptions={metricOptions}
          onChange={handleMetricChange}
        />
      )}

      <div className="PropertyPanel__field">
        <label>{t("decimalPlaces", "Decimal Places")}</label>
        <RadioGroup
          direction="row"
          items={[
            { label: "0.1", value: "1" },
            { label: "0.01", value: "2" },
            { label: "0.001", value: "3" },
          ]}
          value={(item.options.decimalPlaces ?? 1).toString()}
          onChange={(_: ChangeEvent<HTMLInputElement>, value?: string) => {
            if (value) onOptionChange("decimalPlaces", parseInt(value))
          }}
        />
      </div>

      <div className="PropertyPanel__field PropertyPanel__field--toggle">
        <Toggle
          checked={item.options.showLegend ?? true}
          onChange={(e) => onOptionChange("showLegend", e.target.checked)}
        />
        <label>{t("showLegend", "Show Legend")}</label>
      </div>

      <div className="PropertyPanel__field PropertyPanel__field--toggle">
        <Toggle
          checked={item.options.showValues ?? true}
          onChange={(e) => onOptionChange("showValues", e.target.checked)}
        />
        <label>{t("showValues", "Show Values")}</label>
      </div>
    </>
  )
}
